/* eslint-disable react/no-array-index-key */
import moment from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';
import TimelineEventComponent from 'layout/atoms/TimelineEvent';
import styles from './SimpleTimeline.module.scss';
function SimpleTimeline({ groups, events, pixelsPerDay = 60, renderGroupLeft, renderHeaderLeft, labelWidth = 200, timelinePeriod = 'week', scrollToToday = true, }) {
    const timelineOuterRef = useRef(null);
    // Calculate date range
    const dateRange = useMemo(() => {
        if (events.length === 0) {
            const now = moment();
            return {
                start: now.clone().startOf('week').subtract(4, 'weeks'),
                end: now.clone().endOf('week').add(4, 'weeks'),
            };
        }
        const dates = events.map((e) => ({
            start: moment(e.start_time),
            end: moment(e.end_time),
        }));
        let minDate = dates[0].start.clone();
        let maxDate = dates[0].end.clone();
        dates.forEach(({ start, end }) => {
            if (start.isBefore(minDate))
                minDate = start.clone();
            if (end.isAfter(maxDate))
                maxDate = end.clone();
        });
        const today = moment();
        if (today.isAfter(maxDate))
            maxDate = today.clone();
        minDate = minDate.clone().startOf('week').subtract(4, 'weeks');
        maxDate = maxDate.clone().endOf('week').add(4, 'weeks');
        return { start: minDate, end: maxDate };
    }, [events]);
    // Generate day columns
    const days = useMemo(() => {
        const daysList = [];
        const current = dateRange.start.clone();
        while (current.isBefore(dateRange.end) || current.isSame(dateRange.end, 'day')) {
            daysList.push(current.clone());
            current.add(1, 'day');
        }
        return daysList;
    }, [dateRange]);
    // Generate month groups with their day ranges
    const monthGroups = useMemo(() => {
        const groups = [];
        let currentMonthKey = null;
        let monthStartIdx = 0;
        days.forEach((day, idx) => {
            const monthKey = day.format('YYYY-MM');
            if (monthKey !== currentMonthKey) {
                // New month started - save previous month group if exists
                if (currentMonthKey !== null) {
                    const lastGroup = groups[groups.length - 1];
                    lastGroup.endIdx = idx - 1;
                    lastGroup.span = idx - monthStartIdx;
                }
                // Start new month group
                currentMonthKey = monthKey;
                monthStartIdx = idx;
                groups.push({
                    month: day.format('MMMM'),
                    year: day.format('YYYY'),
                    startIdx: idx,
                    endIdx: idx,
                    span: 1,
                });
            }
            else {
                // Same month - update span
                const lastGroup = groups[groups.length - 1];
                lastGroup.endIdx = idx;
                lastGroup.span = idx - lastGroup.startIdx + 1;
            }
        });
        return groups;
    }, [days]);
    // Generate week groups (weeks start on Monday)
    const weekGroups = useMemo(() => {
        const groups = [];
        let currentWeekKey = null;
        let weekStartIdx = 0;
        days.forEach((day, idx) => {
            // Get the Monday of the week this day belongs to
            const monday = day.clone().startOf('week'); // moment.js uses Monday as week start by default with locale
            const weekKey = monday.format('YYYY-MM-DD');
            if (weekKey !== currentWeekKey) {
                // New week started - save previous week group if exists
                if (currentWeekKey !== null) {
                    const lastGroup = groups[groups.length - 1];
                    lastGroup.endIdx = idx - 1;
                    lastGroup.span = idx - weekStartIdx;
                }
                // Start new week group
                currentWeekKey = weekKey;
                weekStartIdx = idx;
                groups.push({
                    weekLabel: monday.format('DD'),
                    startIdx: idx,
                    endIdx: idx,
                    span: 1,
                });
            }
            else {
                // Same week - update span
                const lastGroup = groups[groups.length - 1];
                lastGroup.endIdx = idx;
                lastGroup.span = idx - lastGroup.startIdx + 1;
            }
        });
        return groups;
    }, [days]);
    // Group events by (groupId, day)
    const getEventsForDay = (groupId, day) => {
        return events.filter((event) => {
            if (String(event.groupId) !== String(groupId)) {
                return false;
            }
            const eventStart = moment(event.start_time);
            const eventEnd = moment(event.end_time);
            const dayStart = day.clone().startOf('day');
            const dayEnd = day.clone().endOf('day');
            if (event.renderStartOnly) {
                return eventStart.isSame(dayStart, 'day');
            }
            const endIsAtDayStart = eventEnd.isSame(dayStart);
            if (endIsAtDayStart) {
                return false;
            }
            const overlaps = eventStart.isBefore(dayEnd.clone().add(1, 'millisecond')) && eventEnd.isAfter(dayStart);
            return overlaps;
        });
    };
    // Scroll to today
    useEffect(() => {
        if (!scrollToToday || !timelineOuterRef.current)
            return;
        // Find today's index in the days array
        const today = moment().startOf('day');
        const todayIdx = days.findIndex((day) => day.isSame(today, 'day'));
        if (todayIdx !== -1) {
            // Calculate scroll position: labelWidth + (todayIdx * pixelsPerDay)
            const scrollPosition = labelWidth + todayIdx * pixelsPerDay;
            // Scroll with a small offset to center it better
            timelineOuterRef.current.scrollLeft = scrollPosition - 500;
        }
    }, [days, labelWidth, pixelsPerDay, scrollToToday]);
    return (React.createElement("div", { className: styles.timelineOuter, ref: timelineOuterRef },
        React.createElement("div", { className: styles.timelineWrapper },
            React.createElement("div", { className: styles.monthsHeader },
                React.createElement("div", { className: styles.labelHeader, style: { width: labelWidth, minWidth: labelWidth } }),
                React.createElement("div", { className: styles.monthsRow }, monthGroups.map((group, idx) => (React.createElement("div", { key: idx, className: styles.monthCell, style: {
                        width: `${group.span * pixelsPerDay}px`,
                        minWidth: `${group.span * pixelsPerDay}px`,
                    } }, group.month))))),
            timelinePeriod !== 'day' && (React.createElement("div", { className: styles.weeksHeader },
                React.createElement("div", { className: styles.labelHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft && (React.createElement("div", { className: styles.columnsHeader },
                    React.createElement("div", { className: styles.leftColumnsHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft())))),
                React.createElement("div", { className: styles.weeksRow }, weekGroups.map((group, idx) => (React.createElement("div", { key: idx, className: styles.weekCell, style: {
                        width: `${group.span * pixelsPerDay}px`,
                        minWidth: `${group.span * pixelsPerDay}px`,
                    } }, group.weekLabel)))))),
            timelinePeriod === 'day' && (React.createElement("div", { className: styles.header },
                React.createElement("div", { className: styles.labelHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft && (React.createElement("div", { className: styles.columnsHeader },
                    React.createElement("div", { className: styles.leftColumnsHeader, style: { width: labelWidth, minWidth: labelWidth } }, renderHeaderLeft())))),
                React.createElement("div", { className: styles.daysHeader }, days.map((day, idx) => {
                    const dayOfWeek = day.day();
                    const isWeekend = dayOfWeek === 6 || dayOfWeek === 0; // Saturday or Sunday
                    return (React.createElement("div", { key: idx, className: `${styles.dayHeader} ${isWeekend ? styles.weekend : ''}`, style: { width: `${pixelsPerDay}px`, minWidth: `${pixelsPerDay}px` } },
                        React.createElement("div", { className: styles.dayNumber }, day.format('DD')),
                        React.createElement("div", { className: styles.dayName }, day.format('ddd'))));
                })))),
            React.createElement("div", { className: styles.rowsContainer }, groups.map((group) => (React.createElement("div", { key: group.id, className: styles.row },
                React.createElement("div", { className: styles.labelCell, style: { width: labelWidth, minWidth: labelWidth } }, renderGroupLeft ? renderGroupLeft(group) : React.createElement("span", null, group.title)),
                React.createElement("div", { className: styles.daysRow }, days.map((day, dayIdx) => {
                    const dayEvents = getEventsForDay(group.id, day);
                    return (React.createElement("div", { key: dayIdx, className: styles.dayCell, style: { width: `${pixelsPerDay}px`, minWidth: `${pixelsPerDay}px` } }, dayEvents.map((event) => (React.createElement(TimelineEventComponent, { key: event.id, item: event, getItemProps: () => ({}) })))));
                })))))))));
}
export default SimpleTimeline;
